import React, { useState, useEffect } from 'react';
import './InvoiceTable.css';
import { convertNumberToWordsRu } from './numberToWords';

const InvoiceTable = ({
    orders,
    dateDelivery,
    doctorName,
    doctorCompany,
    pacient,
    companyName,
    address = '',
    inn,
    kpp,
    bankName,
    bik,
    checkingAccount,
    correspondentAccount,
    onBack,
    data
}) => {
    const formattedDate = new Date().toLocaleDateString('ru-RU');

    // Общая сумма
    const totalPrice = orders
        .reduce((sum, o) => sum + (parseFloat(o.price) || 0), 0)
        .toFixed(2);

    const [descriptions, setDescriptions] = useState({});
    
    // Инициализация описаний из заказов
    useEffect(() => {
        const initialDescriptions = {};
        orders.forEach(order => {
            if (order.pacient) {
                initialDescriptions[order.id] = order.pacient;
            }
        });
        setDescriptions(initialDescriptions);
    }, [orders]);
    
    const updateDescription = (orderId, value) => {
        setDescriptions(prev => ({
            ...prev,
            [orderId]: value
        }));
    };

    // Преобразование суммы в слова
    const totalSumWords = convertNumberToWordsRu(totalPrice);

    // Номер первого заказа
    const firstOrderNumber = orders.length > 0 ? orders[0].number || orders[0].id : '—';

    // Генерация HTML для печати
    const generateInvoiceHtml = () => {
        const ordersRows = orders.map((o, i) => `
            <tr>
                <td style="${cellStyle}">${i + 1}</td>
                <td style="${cellStyle}">${o.number || o.id}</td>
                <td style="${cellStyle}; text-align: left;">${descriptions[o.id] || o.pacient || 'Изготовление коронок'}</td>
                <td style="${cellStyle}">${o.price || '0.00'}</td>
            </tr>
        `).join('');

        return `
            <html>
                <head>
                    <meta charset="UTF-8">
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #000; padding: 8px; text-align: center; }
                        th { background-color: #f4f4f4; font-weight: bold; }
                        .requisites-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                        .requisites-table td { border: 1px solid #000; padding: 5px; text-align: left; }
                    </style>
                </head>
                <body>
                    <h2>${companyName || ''}</h2>
                    <p>${address || ''}</p>
                    <table class="requisites-table">
                        <tr>
                            <td>ИНН ${inn || ''}</td>
                            <td>КПП ${kpp || ''}</td>
                            <td rowspan="2">Счет №</td>
                            <td rowspan="2">${checkingAccount || ''}</td>
                        </tr>
                        <tr>
                            <td colspan="2"><strong>Получатель<br/>${companyName || ''}</strong></td>
                        </tr>
                        <tr>
                            <td colspan="2" rowspan="2"><strong>Банк получателя</strong><br/>${bankName || ''}</td>
                            <td>БИК</td>
                            <td>${bik || ''}</td>
                        </tr>
                        <tr>
                            <td>Сч. №</td>
                            <td>${correspondentAccount || ''}</td>
                        </tr>
                    </table>
                    <h2>Счет № ${firstOrderNumber} от ${formattedDate}</h2>
                    <p style="text-align: left; margin-top: 10px; margin-bottom: 5px;"><strong>Заказчик:</strong> ${doctorCompany || doctorName || ''}, ИНН ${inn || ''}, КПП ${kpp || ''}</p>
                    <p style="text-align: left; margin-bottom: 10px;"><strong>Плательщик:</strong></p>
                    <table>
                        <thead>
                            <tr>
                                ${data?.headers?.map(h => `<th>${h}</th>`).join('') || ''}
                            </tr>
                        </thead>
                        <tbody>
                            ${ordersRows}
                            <tr>
                                <td colspan="3" style="text-align: right; font-weight: bold;">Итого:</td>
                                <td style="font-weight: bold;">${totalPrice}</td>
                            </tr>
                        </tbody>
                    </table>
                    <p><strong>Итого: ${totalPrice} руб</strong></p>
                    <p>Всего оказано услуг: ${orders.length}, на сумму ${totalPrice} руб</p>
                    <p><strong>${totalSumWords}</strong></p>
                </body>
            </html>
        `;
    };

    const cellStyle = 'border: 1px solid #000; padding: 8px;';

    // Печать
    const handlePrint = () => {
        const html = generateInvoiceHtml();
        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.print();
    };

    // Скачать HTML
    const handleDownload = () => {
        const html = generateInvoiceHtml();
        const blob = new Blob([html], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${firstOrderNumber}_${formattedDate.replace(/\./g, '_')}.html`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="invoice-table-container">
            {/* Верхние кнопки */}
            <div className="invoice-table-header-buttons">
                <button onClick={onBack} className="invoice-table-button">
                    Назад
                </button>
                <button onClick={handlePrint} className="invoice-table-button">
                    Печать
                </button>
                <button onClick={handleDownload} className="invoice-table-button">
                    Скачать
                </button>
            </div>

            {/* Прокручиваемая часть */}
            <div className="invoice-table-content">
                {/* Название организации и адрес */}
                <h2 className="invoice-company-name">{companyName || ''}</h2>
                <p className="invoice-address">{address || ''}</p>

                {/* Мини-таблица с реквизитами */}
                <table className="invoice-requisites-table-html">
                    <tbody>
                        <tr>
                            <td className="invoice-req-cell">ИНН {inn || ''}</td>
                            <td className="invoice-req-cell">КПП {kpp || ''}</td>
                            <td className="invoice-req-cell" rowSpan="2">Счет №</td>
                            <td className="invoice-req-cell" rowSpan="2">{checkingAccount || ''}</td>
                        </tr>
                        <tr>
                            <td className="invoice-req-cell" colSpan="2"><strong>Получатель<br/>{companyName || ''}</strong></td>
                        </tr>
                        <tr>
                            <td className="invoice-req-cell" colSpan="2" rowSpan="2"><strong>Банк получателя</strong><br/>{bankName || ''}</td>
                            <td className="invoice-req-cell">БИК</td>
                            <td className="invoice-req-cell">{bik || ''}</td>
                        </tr>
                        <tr>
                            <td className="invoice-req-cell">Сч. №</td>
                            <td className="invoice-req-cell">{correspondentAccount || ''}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Заголовок Счет № */}
                <h2 className="invoice-title">
                    Счет № {firstOrderNumber} от {formattedDate}
                </h2>

                {/* Заказчик и Плательщик */}
                <p className="invoice-left-align-text">
                    <strong>Заказчик:</strong> {doctorCompany || doctorName || ''}, ИНН {inn || ''}, КПП {kpp || ''}
                </p>
                <p className="invoice-left-align-text">
                    <strong>Плательщик:</strong>
                </p>

                {/* Таблица с заказами */}
                <div className="invoice-table-table">
                    <div className="invoice-table-row invoice-table-header-row">
                        {data?.headers?.map((header, i) => (
                            <div
                                key={i}
                                className={`invoice-table-cell invoice-table-header-cell ${i === 2 ? 'invoice-table-cell-wide' : ''} ${i === 3 ? 'invoice-table-cell-price' : ''}`}
                            >
                                {header}
                            </div>
                        ))}
                    </div>

                    {orders.map((o, i) => (
                        <div key={o.id || i} className="invoice-table-row">
                            <div className="invoice-table-cell">{i + 1}</div>
                            <div className="invoice-table-cell">{o.number || o.id}</div>
                            <div className="invoice-table-cell invoice-table-cell-wide">
                                <input
                                    type="text"
                                    className="invoice-table-input"
                                    value={descriptions[o.id] !== undefined ? descriptions[o.id] : (o.pacient || 'Изготовление коронок')}
                                    onChange={(e) => updateDescription(o.id, e.target.value)}
                                />
                            </div>
                            <div className="invoice-table-cell invoice-table-cell-price">{o.price || '0.00'}</div>
                        </div>
                    ))}

                    {/* Строка "Итого" */}
                    <div className="invoice-table-row invoice-table-total-row">
                        <div className="invoice-table-cell"></div>
                        <div className="invoice-table-cell"></div>
                        <div className="invoice-table-cell invoice-table-cell-wide invoice-table-total-label">
                            Итого:
                        </div>
                        <div className="invoice-table-cell invoice-table-cell-price invoice-table-total-value">{totalPrice}</div>
                    </div>
                </div>

                {/* Итоговый блок */}
                <div className="invoice-table-summary">
                    <p className="invoice-table-bold">Итого: {totalPrice} руб</p>
                    <p>
                        Всего оказано услуг: {orders.length}, на сумму {totalPrice} руб
                    </p>
                    <p className="invoice-table-bold">{totalSumWords}</p>
                </div>
            </div>
        </div>
    );
};

export default InvoiceTable;

